import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useAvailableSystemsForUser, useUpdateUserSystems } from '@/hooks/useUserSystemAssignments';
import { Loader2 } from 'lucide-react';

interface UserSystemAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  user: {
    user_id: string;
    name: string;
    email: string;
  } | null;
  companyId: string;
  onSuccess?: () => void;
}

export function UserSystemAssignmentModal({
  open,
  onClose,
  user,
  companyId,
  onSuccess
}: UserSystemAssignmentModalProps) {
  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);
  
  const { data: availableSystems = [], isLoading } = useAvailableSystemsForUser(
    user?.user_id,
    companyId
  );
  
  const updateUserSystems = useUpdateUserSystems(user?.user_id || '', companyId);

  // Initialize selected systems when data loads
  useEffect(() => {
    if (availableSystems.length > 0) {
      const userCurrentSystems = availableSystems
        .filter(system => system.user_has_access)
        .map(system => system.id);
      setSelectedSystems(userCurrentSystems);
    }
  }, [availableSystems]);

  const handleSystemToggle = (systemId: string, checked: boolean) => {
    setSelectedSystems(prev => 
      checked 
        ? [...prev, systemId]
        : prev.filter(id => id !== systemId)
    );
  };

  const handleSave = async () => {
    if (!user) return;
    
    console.log('Saving user systems:', { user: user.email, selectedSystems });
    try {
      await updateUserSystems.mutateAsync(selectedSystems);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error updating user systems:', error);
      // Error handled by mutation
    }
  };

  const handleClose = () => {
    setSelectedSystems([]);
    onClose();
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Gerenciar Sistemas</DialogTitle>
          <DialogDescription>
            Selecione os sistemas que <strong>{user.name || user.email}</strong> pode acessar.
            Apenas sistemas incluídos nos planos ativos da empresa estão disponíveis.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Carregando sistemas...</span>
            </div>
          ) : availableSystems.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <p>Nenhum sistema disponível nos planos ativos da empresa.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {availableSystems.map((system) => (
                <div key={system.id} className="flex items-center space-x-3 p-3 border rounded-lg">
                  <Checkbox
                    id={system.id}
                    checked={selectedSystems.includes(system.id)}
                    onCheckedChange={(checked) => 
                      handleSystemToggle(system.id, checked as boolean)
                    }
                  />
                  <div className="flex-1">
                    <Label 
                      htmlFor={system.id}
                      className="text-sm font-medium cursor-pointer"
                    >
                      {system.name}
                    </Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {system.code}
                      </Badge>
                      {system.is_available_in_plan && (
                        <Badge variant="secondary" className="text-xs">
                          Incluído no plano
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSave}
            disabled={updateUserSystems.isPending || isLoading}
          >
            {updateUserSystems.isPending && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}